import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import { useAuthAdmin } from '../Context/useAuthAdmin';
import { useProfileImageUrl } from '../Context/useProfileImageUrl';
import { useOrganizationLogo } from '../hooks/useOrganizationLogo';
import { useAccessScope } from '../hooks/queries/useOrgGroups';
import { fetchBackwardCompatibleGamificationSnapshot } from '../lib/backwardCompatibleAnalytics';
import { fetchWithAuth } from '../lib/apiClient';
import {
  IconBooks,
  IconChartBar,
  IconHome2,
  IconUserCircle,
  IconX,
} from '@tabler/icons-react';
import XPProgressBar from './gamification/XPProgressBar';
import { DEFAULT_LOGO_DIMENSIONS } from '../constants/logo';
import { hasAppSideNav, APP_SIDENAV_TOGGLE_EVENT } from '../lib/sideNavConfig';

const MOBILE_NAV_ITEMS = [
  {
    key: 'home',
    label: 'Home',
    to: '/dashboard',
    match: (pathname) => pathname === '/dashboard' || pathname.startsWith('/dashboard/'),
    icon: IconHome2,
  },
  {
    key: 'catalog',
    label: 'Catalog',
    to: '/courses/all',
    match: (pathname) =>
      pathname === '/catalog' || pathname === '/courses' || pathname.startsWith('/courses/'),
    icon: IconBooks,
  },
  {
    key: 'progress',
    label: 'Progress',
    to: '/my-activity',
    match: (pathname) => pathname === '/my-activity' || pathname.startsWith('/my-activity/'),
    icon: IconChartBar,
  },
];

const MOBILE_ACCOUNT_PANEL_BG = '#f8fafc';
const MOBILE_ACCOUNT_PANEL_SURFACE = '#ffffff';
const MOBILE_ACCOUNT_PANEL_BORDER = '#e2e8f0';
const MOBILE_ACCOUNT_PANEL_TEXT = '#0f172a';
const MOBILE_ACCOUNT_PANEL_MUTED = '#64748b';
const MOBILE_BOTTOM_NAV_OFFSET = 'calc(4.1rem + env(safe-area-inset-bottom, 0px))';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { isSuperAdmin, isAdmin, isCreator, isTrainer, isParent } = useAuthAdmin();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  const headerRef = useRef(null);
  const mobileBottomNavRef = useRef(null);
  const dropdownRef = useRef(null);
  const mobileProfileMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  const navigate = useNavigate();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const debugAuth = import.meta?.env?.DEV && localStorage.getItem('auth_debug') === '1';
  const {
    orgName,
    logo1: orgLogoUrl,
    logo2: orgLogoUrl2,
    logo1Dimensions: orgLogo1Dimensions,
    logo2Dimensions: orgLogo2Dimensions,
    features: orgFeatures,
    shouldHideLogo,
  } = useOrganizationLogo(user?.id);
  const accessScopeQuery = useAccessScope(!!user?.id);
  const effectiveFeatures = useMemo(
    () => accessScopeQuery.data?.effectiveFeatures || orgFeatures || {},
    [accessScopeQuery.data?.effectiveFeatures, orgFeatures]
  );
  
  const {
    data: gamificationSnapshot,
    isLoading: gamificationLoading,
  } = useQuery({
    queryKey: ['user-gamification-snapshot', user?.id],
    // Stored XP, database-counted streak and stored achievements; the progress-based estimate is only a fallback.
    queryFn: async () => {
      try {
        const [xp, streaks, summary] = await Promise.all([
          fetchWithAuth(`/api/gamification/xp/${user.id}`),
          fetchWithAuth(`/api/gamification/streaks/${user.id}`),
          fetchWithAuth('/api/gamification/achievements/summary'),
        ]);
        return {
          userXP: xp,
          streaks: (streaks || []).filter((s) => Number(s.current_count) > 0),
          achievements: { achievements: (summary?.earned || []).map((e) => ({ ...e, name: e.title })) },
        };
      } catch {
        return fetchBackwardCompatibleGamificationSnapshot(user.id, supabase);
      }
    },
    enabled: !!user?.id && dropdownOpen,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const userXP = gamificationSnapshot?.userXP || null;
  const userStreaks = gamificationSnapshot?.streaks || [];
  const userAchievements = gamificationSnapshot?.achievements?.achievements || [];

  // Profile image logic
  const profileImageUrl = useProfileImageUrl(user, user?.user_metadata?.full_name, user?.email);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target)
        && (!mobileProfileMenuRef.current || !mobileProfileMenuRef.current.contains(e.target))
      ) {
        setDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) setNotificationsOpen(false);
    }
    if (dropdownOpen || notificationsOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen, notificationsOpen]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const root = document.documentElement;
    const headerEl = headerRef.current;
    if (!headerEl) return undefined;

    let frameId = null;
    const updateNavbarHeight = () => {
      const nextHeight = Math.ceil(headerEl.getBoundingClientRect().height);
      root.style.setProperty('--app-navbar-height', `${nextHeight}px`);
    };

    const scheduleUpdate = () => {
      if (frameId != null) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateNavbarHeight);
    };

    updateNavbarHeight();
    window.addEventListener('resize', scheduleUpdate);

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => scheduleUpdate())
      : null;
    resizeObserver?.observe(headerEl);

    return () => {
      if (frameId != null) window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, []);

  const loadUnreadCount = useCallback(async () => {
    if (!user?.id) {
      setUnreadCount(0);
      return;
    }
    const { count, error } = await supabase
      .from('notification_recipients')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .is('read_at', null);
    if (!error) setUnreadCount(count || 0);
  }, [user?.id]);

  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    setNotificationsLoading(true);
    const { data, error } = await supabase
      .from('notification_recipients')
      .select('id, read_at, delivered_at, notifications (id, title, body, created_at, action_url)')
      .eq('user_id', user.id)
      .order('delivered_at', { ascending: false })
      .limit(8);
    if (!error) setNotifications(data || []);
    setNotificationsLoading(false);
  }, [user?.id]);

  const markAllNotificationsRead = useCallback(async () => {
    if (!user?.id) return;
    await supabase
      .from('notification_recipients')
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .is('read_at', null);
    await loadNotifications();
    await loadUnreadCount();
  }, [loadNotifications, loadUnreadCount, user?.id]);

  const handleNotificationClick = useCallback(async (notificationRow) => {
    if (!notificationRow) return;
    if (!notificationRow.read_at) {
      await supabase
        .from('notification_recipients')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationRow.id);
      await loadUnreadCount();
    }
    const actionUrl = notificationRow.notifications?.action_url;
    if (actionUrl) {
      if (actionUrl.startsWith('http')) {
        window.open(actionUrl, '_blank', 'noopener');
            } else {
        navigate(actionUrl);
      }
    }
  }, [loadUnreadCount, navigate]);

  useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  useEffect(() => {
    if (notificationsOpen) {
      loadNotifications();
      loadUnreadCount();
    }
  }, [loadNotifications, loadUnreadCount, notificationsOpen]);

  const bypassPaidPlans = Boolean(effectiveFeatures?.bypass_plans);
  const canAccessLanguageLab = effectiveFeatures?.language_tools !== false && effectiveFeatures?.language_lab !== false;
  const canShowAiAvatarButton = effectiveFeatures?.xr_dashboard !== false;

  useEffect(() => {
    if (effectiveFeatures && typeof window !== 'undefined') {
      try {
        localStorage.setItem('org_features_cache', JSON.stringify(effectiveFeatures));
      } catch { /* ignore */ }
    }
  }, [effectiveFeatures]);

  useEffect(() => {
    if (debugAuth && gamificationSnapshot) {
      console.log('Shared gamification snapshot:', gamificationSnapshot);
    }
  }, [debugAuth, gamificationSnapshot]);

  // Close mobile menu on route change
  const location = useLocation();
  const isParentUser = typeof isParent === 'function' && isParent();
  const sideNavPresent = hasAppSideNav(location.pathname);
  const isXrNavbar = location.pathname === '/dashboard/xr'
    || location.pathname.startsWith('/edit-virtual-world')
    || location.pathname.startsWith('/create-virtual-world');
  const homeRoute = user ? (isParentUser ? '/parent_dashboard' : '/dashboard') : 'https://astra-xr.com';
  const primaryLogoDimensions = orgLogoUrl ? orgLogo1Dimensions : DEFAULT_LOGO_DIMENSIONS;
  const secondaryLogoDimensions = orgLogo2Dimensions || orgLogo1Dimensions || DEFAULT_LOGO_DIMENSIONS;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleViewportChange = (event) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);
      return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    setNotificationsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if ((!dropdownOpen && !mobileMenuOpen) || !isMobileViewport || typeof document === 'undefined') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [dropdownOpen, isMobileViewport, mobileMenuOpen]);

  const mobileProfileOverlayOpen = isMobileViewport && dropdownOpen;
  const mobileMenuOverlayOpen = isMobileViewport && mobileMenuOpen;
  const mobileOverlayOpen = mobileProfileOverlayOpen || mobileMenuOverlayOpen;
  const showMobileBottomNav = Boolean(user) && !isParentUser && !mobileMenuOverlayOpen;
  const isProfileNavActive = dropdownOpen || location.pathname === '/profile';

  // Expose the fixed mobile bottom nav's real height (icons + safe-area
  // inset) as a CSS var so other fixed/absolute mobile UI (e.g. the app
  // side nav drawer) can size itself to stop above it instead of running
  // underneath.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const root = document.documentElement;

    if (!showMobileBottomNav) {
      root.style.setProperty('--app-mobile-bottom-nav-height', '0px');
      return undefined;
    }

    const navEl = mobileBottomNavRef.current;
    if (!navEl) return undefined;

    let frameId = null;
    const updateBottomNavHeight = () => {
      const nextHeight = Math.ceil(navEl.getBoundingClientRect().height);
      root.style.setProperty('--app-mobile-bottom-nav-height', `${nextHeight}px`);
    };

    const scheduleUpdate = () => {
      if (frameId != null) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateBottomNavHeight);
    };

    updateBottomNavHeight();
    window.addEventListener('resize', scheduleUpdate);

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => scheduleUpdate())
      : null;
    resizeObserver?.observe(navEl);

    return () => {
      if (frameId != null) window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      root.style.setProperty('--app-mobile-bottom-nav-height', '0px');
    };
  }, [showMobileBottomNav]);

  const handleProfileToggle = () => {
    setNotificationsOpen(false);
    setMobileMenuOpen(false);
    setDropdownOpen((open) => !open);
  };

  const handleMenuNavigate = (to) => {
    setDropdownOpen(false);
    navigate(to);
  };

  const handleLogout = async () => {
    setDropdownOpen(false);

    try {
      await logout();
    } catch (logoutError) {
      console.warn('[Navbar] Logout failed', logoutError);
    }

    navigate('/login');
  };

  const buildPrimaryNavClass = (isActive) => {
    if (isXrNavbar) {
      return `block rounded-full px-4 py-2 text-sm font-semibold transition ${
        isActive
          ? 'bg-white/10 text-sky-300 shadow-sm'
          : 'text-white/90 hover:bg-white/5 hover:text-sky-300'
      }`;
    }

    if (mobileMenuOpen) {
      return `block rounded-2xl px-4 py-3 text-sm font-semibold transition ${
        isActive
          ? 'bg-d-primary/10 text-d-primary'
          : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
      }`;
    }

    return `block rounded-full px-4 py-2 text-sm font-semibold transition ${
      isActive
        ? 'bg-d-primary/10 text-d-primary shadow-sm'
        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
    }`;
  };

  const renderProfileMenuContent = ({ mobile = false } = {}) => {
    const sectionCardClass = mobile
      ? 'rounded-3xl border border-slate-200 bg-white shadow-sm'
      : 'rounded-2xl border border-slate-200 bg-white shadow-sm';
    const sectionCardStyle = mobile
      ? {
          backgroundColor: MOBILE_ACCOUNT_PANEL_SURFACE,
          borderColor: MOBILE_ACCOUNT_PANEL_BORDER,
          color: MOBILE_ACCOUNT_PANEL_TEXT,
        }
      : undefined;
    const neutralButtonStyle = mobile
      ? {
          backgroundColor: MOBILE_ACCOUNT_PANEL_SURFACE,
          borderColor: MOBILE_ACCOUNT_PANEL_BORDER,
          color: MOBILE_ACCOUNT_PANEL_TEXT,
        }
      : undefined;
    const adminButtonStyle = mobile
      ? {
          backgroundColor: '#f5f3ff',
          borderColor: '#ddd6fe',
          color: '#6d28d9',
        }
      : undefined;
    const dangerButtonStyle = mobile
      ? {
          backgroundColor: '#fff1f2',
          borderColor: '#fecdd3',
          color: '#be123c',
        }
      : undefined;
    const baseItemClass = mobile
      ? 'w-full rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition'
      : 'w-full rounded-lg border px-2.5 py-1.5 text-left text-xs font-semibold transition';
    const neutralItemClass = `${baseItemClass} border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-100`;
    const adminItemClass = `${baseItemClass} border-violet-200 bg-violet-50 text-violet-700 hover:border-violet-300 hover:bg-violet-100`;
    const dangerItemClass = `${baseItemClass} border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-300 hover:bg-rose-100`;
    const sectionSpacingClass = mobile ? 'space-y-2' : 'space-y-1.5';
    const contentSpacingClass = mobile ? 'space-y-4' : 'space-y-2.5';
    const headerCardPaddingClass = mobile ? 'p-4' : 'p-2.5';
    const sectionCardPaddingClass = mobile ? 'p-4' : 'p-2.5';
    const actionCardPaddingClass = mobile ? 'p-3' : 'p-2';
    const identityImageClass = mobile ? 'h-14 w-14 rounded-2xl' : 'h-10 w-10 rounded-lg';
    const identityRowGapClass = mobile ? 'gap-3' : 'gap-2.5';
    const nameTextClass = mobile ? 'text-base font-semibold text-slate-900' : 'text-[13px] font-semibold text-slate-900';
    const emailTextClass = mobile ? 'mt-0.5 text-sm text-slate-500' : 'mt-0.5 text-[11px] text-slate-500';
    const hasAchievements = !isParentUser && userAchievements && userAchievements.length > 0;
    const hasStreaks = !isParentUser && userStreaks && userStreaks.length > 0;
    const showHighlightsRow = hasAchievements || hasStreaks;

    return (
      <div className={contentSpacingClass}>
        <div className={`${sectionCardClass} ${headerCardPaddingClass}`} style={sectionCardStyle}>
          <div className={`flex items-center ${identityRowGapClass}`}>
            <img
              src={profileImageUrl || '/logo.webp'}
              alt="Profile"
              className={`${identityImageClass} object-cover ring-2 ring-slate-200`}
              loading="lazy"
              decoding="async"
              width={64}
              height={64}
            />
            <div>
              <div
                className={nameTextClass}
                style={mobile ? { color: MOBILE_ACCOUNT_PANEL_TEXT } : undefined}
              >
                {user?.user_metadata?.full_name || user?.email}
              </div>
              <div
                className={emailTextClass}
                style={mobile ? { color: MOBILE_ACCOUNT_PANEL_MUTED } : undefined}
              >
                {user?.email}
              </div>
            </div>
          </div>
        </div>

        {!isParentUser && gamificationLoading ? (
          <div className={`${sectionCardClass} ${sectionCardPaddingClass}`} style={sectionCardStyle}>
            <div className="flex items-center gap-2">
              <div className={`h-6 w-6 animate-pulse rounded-full ${mobile ? 'bg-slate-200' : 'bg-gray-200'}`} />
              <div>
                <div className={`mb-1 h-3 w-20 animate-pulse rounded ${mobile ? 'bg-slate-200' : 'bg-gray-200'}`} />
                <div className={`h-2 w-16 animate-pulse rounded ${mobile ? 'bg-slate-200' : 'bg-gray-200'}`} />
              </div>
            </div>
          </div>
        ) : !isParentUser && userXP ? (
          <div
            className={`rounded-3xl border border-slate-200 bg-white shadow-sm ${sectionCardPaddingClass}`}
            style={sectionCardStyle}
          >
            <XPProgressBar
              userXP={userXP}
              showDetails={true}
              animate={false}
              compact={true}
              disableCelebrations={true}
            />
            <div className={`mt-1 flex items-center justify-between text-[10px] ${mobile ? 'text-slate-400' : 'text-slate-600'}`}>
              <span>Level {userXP.level_number || 1}</span>
              <span>{Math.round(userXP.total_xp || 0)} total XP</span>
            </div>
          </div>
        ) : !isParentUser ? (
          <div
            className={`rounded-3xl border border-slate-200 bg-white shadow-sm ${sectionCardPaddingClass}`}
            style={sectionCardStyle}
          >
            <div className="text-[11px] font-medium text-slate-900">Level 1 - Getting Started</div>
            <div className="text-[10px] text-slate-600">0 total XP • Complete activities to earn XP!</div>
          </div>
        ) : null}

        {showHighlightsRow && (
          <div className={`grid ${mobile ? 'gap-3' : 'gap-2'} ${hasAchievements && hasStreaks ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {hasAchievements && (
              <div
                className={`${mobile ? 'rounded-3xl' : 'rounded-2xl'} border border-slate-200 bg-white shadow-sm ${mobile ? 'p-3' : 'p-2'}`}
                style={sectionCardStyle}
              >
                <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  Recent Achievements
                </div>
                <div className="flex flex-wrap gap-1">
                  {userAchievements.slice(0, 3).map((achievement, index) => (
                    <div
                      key={index}
                      className="flex max-w-full items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-700"
                      title={achievement.description || achievement.name}
                    >
                      <span className="text-[10px]">{achievement.icon || '🏆'}</span>
                      <span className="truncate">{achievement.name || achievement.achievement_type}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {hasStreaks && (
              <div
                className={`${mobile ? 'rounded-3xl' : 'rounded-2xl'} border border-slate-200 bg-white shadow-sm ${mobile ? 'p-3' : 'p-2'}`}
                style={sectionCardStyle}
              >
                <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  Active Streaks
                </div>
                <div className="flex flex-wrap gap-1">
                  {userStreaks.slice(0, 3).map((streak, index) => (
                    <div
                      key={index}
                      className="flex max-w-full items-center gap-0.5 rounded-full bg-orange-50 px-1.5 py-0.5 text-[10px] text-orange-700"
                    >
                      <span className="text-[10px]">🔥</span>
                      <span className="truncate">
                        {streak.current_count || 0} {(streak.streak_type || 'day').replace('_', ' ')}
                      </span>
                    </div>
                  ))}
                </div>
                {userStreaks.length > 3 && (
                  <div className="mt-1 text-[9px] text-slate-500">
                    +{userStreaks.length - 3} more streak{userStreaks.length - 3 > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!isParentUser && (userXP || userStreaks.length > 0 || userAchievements.length > 0) && (
          <div
            className={`rounded-3xl border border-slate-200 bg-white shadow-sm ${sectionCardPaddingClass}`}
            style={sectionCardStyle}
          >
            <div className="mb-1 text-[9px] uppercase tracking-wide text-slate-500">Learning Progress</div>
            <div className="flex justify-between text-[10px]">
              <span className="text-slate-700">📈 {userXP ? `Level ${userXP.level_number || 1}` : 'Starting'}</span>
              <span className="text-slate-700">🔥 {userStreaks.length} active streak{userStreaks.length !== 1 ? 's' : ''}</span>
              <span className="text-slate-700">🏆 {userAchievements.length} achievement{userAchievements.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        )}

        {isParentUser ? (
          <div className={sectionCardClass} style={sectionCardStyle}>
            <div className={actionCardPaddingClass}>
              <div className={sectionSpacingClass}>
                <button className={neutralItemClass} style={neutralButtonStyle} onClick={() => handleMenuNavigate('/parent_dashboard')}>
                  Parent Dashboard
                </button>
                <button className={neutralItemClass} style={neutralButtonStyle} onClick={() => handleMenuNavigate('/about')}>
                  About
                </button>
                <button className={neutralItemClass} style={neutralButtonStyle} onClick={() => handleMenuNavigate('/contact')}>
                  Contact
                </button>
              </div>
            </div>
          </div>
        ) : (
          !sideNavPresent && (
            <div className={sectionCardClass} style={sectionCardStyle}>
              <div className={actionCardPaddingClass}>
                <div className={sectionSpacingClass}>
                  {isSuperAdmin && isSuperAdmin() ? (
                    <button className={adminItemClass} style={adminButtonStyle} onClick={() => handleMenuNavigate('/admin')}>
                      Admin Dashboard
                    </button>
                  ) : null}
                  {(isAdmin && isAdmin()) || (isSuperAdmin && isSuperAdmin()) ? (
                    <button className={adminItemClass} style={adminButtonStyle} onClick={() => handleMenuNavigate('/messages')}>
                      Messages Inbox
                    </button>
                  ) : null}
                  {(isAdmin && isAdmin()) || (isCreator && isCreator()) || (isTrainer && isTrainer()) ? (
                    <button className={adminItemClass} style={adminButtonStyle} onClick={() => handleMenuNavigate('/learning-delivery')}>
                      Learning &amp; Delivery
                    </button>
                  ) : null}
                  {canAccessLanguageLab ? (
                    <button className={neutralItemClass} style={neutralButtonStyle} onClick={() => handleMenuNavigate('/language-lab')}>
                      Language Lab
                    </button>
                  ) : null}
                  {(isAdmin && isAdmin()) || (isCreator && isCreator()) ? (
                    <button className={neutralItemClass} style={neutralButtonStyle} onClick={() => handleMenuNavigate('/admin/org-branding')}>
                      Organization Control
                    </button>
                  ) : null}
                  <button className={neutralItemClass} style={neutralButtonStyle} onClick={() => handleMenuNavigate('/profile')}>
                    Settings
                  </button>
                  {!bypassPaidPlans && (
                    <button className={neutralItemClass} style={neutralButtonStyle} onClick={() => handleMenuNavigate('/plans')}>
                      Plans &amp; Billing
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        )}

        <div className={sectionCardClass} style={sectionCardStyle}>
          <div className={actionCardPaddingClass}>
            <button className={dangerItemClass} style={dangerButtonStyle} onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`border-b border-transparent px-4 py-3 sm:px-6 ${
          isXrNavbar
            ? 'shadow-none'
            : 'bg-transparent'
        }`}
        style={{
          zIndex: mobileOverlayOpen ? 1200 : 1000,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          maxWidth: '100vw',
          ...(isXrNavbar
            ? {
                background: 'rgba(2,6,23,0.7)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
                borderBottomColor: 'rgba(56,189,248,0.18)',
                boxShadow: '0 1px 0 0 rgba(56,189,248,0.12), 0 4px 24px rgba(2,6,23,0.3)',
              }
            : {
                background: 'linear-gradient(180deg, rgba(248,250,252,0.78) 0%, rgba(248,250,252,0.46) 100%)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
                borderBottomColor: 'rgba(148,163,184,0.14)',
              }),
        }}
      >
        <div
          className={`flex items-center justify-between rounded-[1.75rem] border ring-1 ring-inset px-3 py-2 md:grid md:grid-cols-[auto_1fr_auto] md:gap-4 md:px-5 md:py-3 ${
            isXrNavbar
              ? 'border-sky-400/20 ring-sky-300/10'
              : 'border-white/80 bg-white/90 ring-d-primary/10 shadow-[0_18px_45px_rgba(15,23,42,0.08)]'
          }`}
          style={isXrNavbar ? undefined : {
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
          }}
        >
          <div className="flex items-center justify-start">
            <button
              className={`rounded-2xl border p-2 shadow-sm focus:outline-none focus:ring-2 md:hidden ${
                isXrNavbar
                  ? 'border-sky-300/30 bg-white/10 focus:ring-sky-400/60'
                  : 'border-d-primary/15 bg-white/85 text-d-primary focus:ring-d-primary/40'
              }`}
              onClick={() => {
                setDropdownOpen(false);
                // Sidebar pages: the menu button opens the side nav drawer (its
                // floating opener is hidden on phones), not the reduced main menu.
                if (sideNavPresent && user) {
                  setMobileMenuOpen(false);
                  window.dispatchEvent(new CustomEvent(APP_SIDENAV_TOGGLE_EVENT));
                  return;
                }
                setMobileMenuOpen((open) => !open);
              }}
              aria-label={sideNavPresent && user ? 'Open navigation' : 'Open main menu'}
              aria-expanded={mobileMenuOpen}
            >
              <svg className={`h-6 w-6 ${isXrNavbar ? 'text-slate-50' : 'text-d-primary'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            <a href={homeRoute} className="hidden items-center gap-3 md:flex">
              {shouldHideLogo ? (
                <div aria-hidden="true" className="h-12 w-28 shrink-0" />
              ) : (
                <>
                  <img
                    src={orgLogoUrl || '/logo.webp'}
                    alt={orgName ? `${orgName} Logo` : 'Astra XR Logo'}
                    className="h-12 w-auto transition hover:opacity-80"
                    width={primaryLogoDimensions.width}
                    height={primaryLogoDimensions.height}
                    loading="eager"
                    fetchpriority="high"
                    decoding="sync"
                  />
                  {orgLogoUrl2 && (
                    <img
                      src={orgLogoUrl2}
                      alt={orgName ? `${orgName} Logo 2` : 'Organization Logo 2'}
                      className="h-12 w-auto transition hover:opacity-80"
                      width={secondaryLogoDimensions.width}
                      height={secondaryLogoDimensions.height}
                      loading="eager"
                      fetchpriority="auto"
                      decoding="auto"
                    />
                  )}
                </>
              )}
            </a>
          </div>

          <div className="flex items-center justify-center md:justify-center">
            <a href={homeRoute} className="flex items-center gap-2 md:hidden">
              {shouldHideLogo ? (
                <div aria-hidden="true" className="h-10 w-24 shrink-0" />
              ) : (
                <>
                  <img
                    src={orgLogoUrl || '/logo.webp'}
                    alt={orgName ? `${orgName} Logo` : 'Astra XR Logo'}
                    className={`w-auto transition hover:opacity-80 ${orgLogoUrl2 ? 'h-8' : 'h-10'}`}
                    width={primaryLogoDimensions.width}
                    height={primaryLogoDimensions.height}
                    loading="eager"
                    fetchpriority="high"
                    decoding="sync"
                  />
                  {orgLogoUrl2 && (
                    <img
                      src={orgLogoUrl2}
                      alt={orgName ? `${orgName} Logo 2` : 'Organization Logo 2'}
                      className="h-8 w-auto transition hover:opacity-80"
                      width={secondaryLogoDimensions.width}
                      height={secondaryLogoDimensions.height}
                      loading="eager"
                      fetchpriority="auto"
                      decoding="auto"
                    />
                  )}
                </>
              )}
            </a>

            <nav
              className={`absolute left-0 right-0 top-full z-40 flex-col font-bold shadow-md transition-all duration-200 ease-in-out md:static md:z-auto md:flex md:flex-row md:items-center md:justify-center md:gap-2 md:shadow-none ${
                mobileMenuOpen ? 'flex' : 'hidden'
              } ${
                isXrNavbar
                  ? 'md:border-0 md:bg-transparent'
                  : mobileMenuOpen
                    ? 'bg-white/95 text-slate-900 md:bg-transparent'
                    : 'bg-white text-blue-900 md:rounded-full md:border md:border-slate-200/70 md:bg-white/70 md:px-2 md:py-1'
              }`}
              style={{
                marginTop: 0,
                ...(mobileMenuOpen
                  ? {
                      background: isXrNavbar ? 'rgba(2,6,23,0.96)' : 'rgba(255,255,255,0.96)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      borderTop: `1px solid ${isXrNavbar ? 'rgba(56,189,248,0.15)' : 'rgba(226,232,240,0.95)'}`,
                      boxShadow: isXrNavbar ? '0 24px 60px rgba(2,6,23,0.38)' : '0 18px 36px rgba(15,23,42,0.08)',
                    }
                  : {}),
              }}
            >
              {isParentUser ? (
                <Link
                  to="/parent_dashboard"
                  className={buildPrimaryNavClass(location.pathname === '/parent_dashboard')}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Parent Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/dashboard"
                    className={buildPrimaryNavClass(location.pathname === '/dashboard')}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/courses"
                    className={buildPrimaryNavClass(location.pathname.startsWith('/courses'))}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Courses
                  </Link>
                </>
              )}
              {user && !isParentUser && canShowAiAvatarButton && (
                <Link
                  to="/dashboard/xr/ai-avatar"
                  className={buildPrimaryNavClass(location.pathname === '/dashboard/xr/ai-avatar')}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  AI Avatar
                </Link>
              )}
              {user && !isParentUser && !bypassPaidPlans && (
                <Link
                  to="/plans"
                  className={buildPrimaryNavClass(location.pathname === '/plans')}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Plans
                </Link>
              )}
            </nav>
          </div>

          <div className="flex items-center justify-end">
            {user ? (
              <>
                {!isParentUser && (
                  <div className="relative mr-2 md:ml-4" ref={notificationsRef}>
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        setNotificationsOpen((open) => !open);
                      }}
                      className={`relative flex h-10 w-10 items-center justify-center rounded-full border transition focus:outline-none ${
                        isXrNavbar
                          ? 'border border-sky-300/25 hover:border-sky-300/60'
                          : 'border-slate-200 bg-white/85 shadow-sm hover:border-d-primary/20 hover:bg-d-primary/5'
                      }`}
                      style={isXrNavbar ? { background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' } : {}}
                      aria-label="Open notifications"
                      aria-expanded={notificationsOpen}
                    >
                      <svg className={`h-5 w-5 ${isXrNavbar ? 'text-white' : 'text-slate-700'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V4a2 2 0 10-4 0v1.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      {unreadCount > 0 && (
                        <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] text-white">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </button>
                    {notificationsOpen && (
                      <div className="absolute right-0 z-50 mt-3 w-80 overflow-hidden rounded-[1.5rem] border border-white/80 bg-white/95 py-2 shadow-[0_22px_55px_rgba(15,23,42,0.14)] backdrop-blur-xl">
                        <div className="flex items-center justify-between border-b border-slate-200/80 px-4 py-3">
                          <div className="text-sm font-semibold text-blue-900">Notifications</div>
                          <button
                            className="text-xs font-semibold text-d-primary hover:text-d-primary-dim"
                            onClick={markAllNotificationsRead}
                            disabled={!unreadCount}
                          >
                            Mark all read
                          </button>
                        </div>
                        {notificationsLoading ? (
                          <div className="px-4 py-4 text-sm text-gray-500">Loading notifications...</div>
                        ) : notifications.length === 0 ? (
                          <div className="px-4 py-4 text-sm text-gray-500">No notifications yet.</div>
                        ) : (
                          <div className="max-h-80 overflow-y-auto">
                            {notifications.map((row) => (
                              <button
                                key={row.id}
                                className={`w-full border-b border-slate-100 px-4 py-3 text-left last:border-b-0 hover:bg-slate-50 ${row.read_at ? 'bg-white' : 'bg-d-primary/5'}`}
                                onClick={() => handleNotificationClick(row)}
                              >
                                <div className="text-sm font-semibold text-gray-900">
                                  {row.notifications?.title || 'Notification'}
                                </div>
                                {row.notifications?.body && (
                                  <div className="mt-1 line-clamp-2 text-xs text-gray-600">
                                    {row.notifications.body}
                                  </div>
                                )}
                                <div className="mt-2 text-[10px] text-gray-400">
                                  {new Date(row.notifications?.created_at || row.delivered_at).toLocaleString()}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        <button
                          className="w-full px-4 py-3 text-left text-sm font-semibold text-d-primary hover:bg-slate-50"
                          onClick={() => {
                            setNotificationsOpen(false);
                            navigate('/notifications');
                          }}
                        >
                          View all notifications
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="relative md:ml-4" ref={dropdownRef}>
                  <button
                    onClick={handleProfileToggle}
                    className={`hidden h-10 w-10 items-center justify-center rounded-full border transition focus:outline-none md:flex ${
                      isXrNavbar
                        ? 'border border-sky-300/30 hover:border-sky-300/70'
                        : 'border-slate-200 bg-white/85 shadow-sm hover:border-d-primary/20 hover:bg-d-primary/5'
                    }`}
                    style={isXrNavbar ? { background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' } : {}}
                    aria-label="Open user menu"
                    aria-expanded={dropdownOpen}
                  >
                    <span className="sr-only">Open user menu</span>
                    <img
                      src={profileImageUrl || '/logo.webp'}
                      alt="Profile"
                      className="h-8 w-8 rounded-full object-cover"
                      loading="lazy"
                      decoding="async"
                      width={64}
                      height={64}
                    />
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 z-50 mt-3 hidden max-h-[80vh] w-[19rem] overflow-y-auto rounded-[1.35rem] border border-d-primary/15 bg-white/95 p-2.5 ring-1 ring-inset ring-d-primary/10 shadow-[0_22px_55px_rgba(15,23,42,0.14)] backdrop-blur-xl md:block">
                      {renderProfileMenuContent()}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <button
                className="mr-3 rounded-full bg-gradient-to-r from-d-primary to-d-primary-container px-4 py-2 font-semibold text-white shadow-lg shadow-d-primary/20 transition hover:shadow-xl md:ml-4"
                onClick={() => navigate('/login')}
              >
                Login
              </button>
            )}
          </div>
        </div>
      </header>

      {dropdownOpen && (
        <div
          id="mobile-profile-menu"
          ref={mobileProfileMenuRef}
          className="fixed inset-x-0 top-0 z-[1100] md:hidden"
          style={{ bottom: MOBILE_BOTTOM_NAV_OFFSET }}
          role="dialog"
          aria-modal="true"
          aria-label="Profile menu"
        >
          <button
            className="absolute inset-0 bg-slate-900/15"
            style={{ backgroundColor: 'rgba(15, 23, 42, 0.14)' }}
            onClick={() => setDropdownOpen(false)}
            aria-label="Close profile menu"
          />
          <div
            className="absolute inset-0 overflow-y-auto bg-slate-50 text-slate-900"
            style={{
              backgroundColor: MOBILE_ACCOUNT_PANEL_BG,
              color: MOBILE_ACCOUNT_PANEL_TEXT,
            }}
          >
            <div
              className={`sticky top-0 z-10 border-b px-4 pb-4 ${
                isXrNavbar ? 'border-sky-200/40 bg-white' : 'border-slate-200 bg-white'
              }`}
              style={{
                paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))',
                backgroundColor: MOBILE_ACCOUNT_PANEL_SURFACE,
                borderColor: MOBILE_ACCOUNT_PANEL_BORDER,
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div
                    className={`text-xs font-semibold uppercase tracking-[0.18em] ${isXrNavbar ? 'text-sky-300/80' : 'text-slate-400'}`}
                    style={!isXrNavbar ? { color: MOBILE_ACCOUNT_PANEL_MUTED } : { color: '#0369a1' }}
                  >
                    Profile
                  </div>
                  <div className="mt-1 text-lg font-semibold" style={{ color: MOBILE_ACCOUNT_PANEL_TEXT }}>
                    Account &amp; navigation
                  </div>
                </div>
                <button
                  onClick={() => setDropdownOpen(false)}
                  className={`flex h-11 w-11 items-center justify-center rounded-2xl transition ${
                    isXrNavbar
                      ? 'border border-sky-200 bg-white text-slate-900 hover:border-sky-300'
                      : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-100'
                  }`}
                  style={{
                    backgroundColor: MOBILE_ACCOUNT_PANEL_SURFACE,
                    borderColor: MOBILE_ACCOUNT_PANEL_BORDER,
                    color: MOBILE_ACCOUNT_PANEL_TEXT,
                  }}
                  aria-label="Close profile menu"
                >
                  <IconX className="h-5 w-5" stroke={2} />
                </button>
              </div>
            </div>
            <div
              className="px-4 py-4"
              style={{ paddingBottom: '1rem' }}
            >
              {renderProfileMenuContent({ mobile: true })}
            </div>
          </div>
        </div>
      )}

      {mobileMenuOpen && (
        <button
          type="button"
          className="fixed inset-0 z-[1190] bg-slate-200/35 backdrop-blur-[5px] md:hidden"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close navigation menu"
        />
      )}

      {showMobileBottomNav && (
        <nav
          ref={mobileBottomNavRef}
          className={`fixed inset-x-0 bottom-0 z-[1001] border-t md:hidden ${
            isXrNavbar
              ? 'border-sky-300/15 bg-slate-950/90'
              : 'border-slate-200 bg-white/95'
          } backdrop-blur-xl`}
          style={{
            paddingTop: '0.4rem',
            paddingBottom: 'calc(0.45rem + env(safe-area-inset-bottom, 0px))',
          }}
          aria-label="Mobile navigation"
        >
          <div className="mx-auto flex max-w-md items-center justify-around gap-1 px-3">
            {MOBILE_NAV_ITEMS.map(({ key, label, to, match, icon }) => {
              const isActive = match(location.pathname);

              return (
                <Link
                  key={key}
                  to={to}
                  onClick={() => {
                    setDropdownOpen(false);
                    setNotificationsOpen(false);
                    setMobileMenuOpen(false);
                  }}
                  className="flex min-w-0 flex-1 items-center justify-center py-1 transition"
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={label}
                >
                  <span
                    className={`flex h-10 w-10 items-center justify-center rounded-xl border transition ${
                      isXrNavbar
                        ? isActive
                          ? 'border-sky-500 bg-sky-500 text-white shadow-sm shadow-sky-500/25'
                          : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:bg-white/10 hover:text-white'
                        : isActive
                          ? 'border-blue-600 bg-blue-600 text-white shadow-sm shadow-blue-600/20'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    {React.createElement(icon, { className: 'h-5 w-5', stroke: 2 })}
                  </span>
                </Link>
              );
            })}

            <button
              type="button"
              onClick={handleProfileToggle}
              className="flex min-w-0 flex-1 items-center justify-center py-1 transition"
              aria-label="Profile"
              aria-expanded={dropdownOpen}
              aria-controls="mobile-profile-menu"
            >
              <span
                className={`flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl border transition ${
                  isXrNavbar
                    ? isProfileNavActive
                      ? 'border-sky-500 bg-sky-500 text-white shadow-sm shadow-sky-500/25'
                      : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:bg-white/10 hover:text-white'
                    : isProfileNavActive
                      ? 'border-blue-600 bg-blue-600 text-white shadow-sm shadow-blue-600/20'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                {profileImageUrl ? (
                  <img
                    src={profileImageUrl}
                    alt="Profile"
                    className={`h-7 w-7 rounded-lg object-cover ${
                      isProfileNavActive ? 'ring-1 ring-white/80' : ''
                    }`}
                    loading="lazy"
                    decoding="async"
                    width={64}
                    height={64}
                  />
                ) : (
                  <IconUserCircle className="h-6 w-6" stroke={1.8} />
                )}
              </span>
            </button>
          </div>
        </nav>
      )}
    </>
  );
} 